import { Button, Checkbox, DatePicker, Input, Select, Tag } from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';
import { useForm, Controller, FieldValues } from 'react-hook-form';

import { Props, Field } from './FiltersForm.model';

import styles from './FiltersForm.module.scss';
import { useEffect } from 'react';

const SearchForm = <T extends FieldValues>({
	activeForm,
	clearAllLabel,
	defaultValues,
	fields,
	headerLabel,
	submitLabel,
	stickyValues,
	onResetFilters,
	onSubmit,
}: Props<T>) => {
	const { control, handleSubmit, reset } = useForm();
	const intl = useIntl();

	const { Option } = Select;

	useEffect(() => {
		activeForm && reset(defaultValues);
	}, [activeForm])

	const clickClearAll = () => {
		reset(defaultValues);
		onResetFilters && onResetFilters();
	}

	const renderFieldByType = (searchField: Field<T>, field: any) => {
		switch (searchField.type) {
			case "input":
				return <Input {...field} placeholder={searchField.placeholder || intl.formatMessage({ id: `formPlaceholders.${searchField.name}` })} />;
			case "select":
				return (
					<Select
						{...field}
						placeholder={searchField.placeholder || intl.formatMessage({ id: `formPlaceholders.${searchField.name}` })}
						style={{ width: "100%" }}
					>
						{searchField.options &&
							searchField.options.optionList.map((option: any, index: number) => (
								<Option
									key={`option${searchField.name}${index}`}
									value={option.value}
								>
									{searchField.options?.type === 'codeBased' ?
										option.label :
										intl.formatMessage({ id: `${searchField.options?.alternateTranslationPrefix ? searchField.options.alternateTranslationPrefix : searchField.name}.${option.label}` })}
								</Option>
							))}
					</Select>
				);
			case "tags":
				return (
					<Select
						{...field}
						mode="tags"
						placeholder={searchField.placeholder || intl.formatMessage({ id: `formPlaceholders.${searchField.name}` })}
						style={{ width: "100%" }}
						filterOption={(input, option: any) =>
							option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
						}
						tagRender={({ label }) => (
							<Tag
								style={{
									border: "none",
									textTransform: "uppercase",
								}}
							>
								{label}
							</Tag>
						)}
					>
						{
							searchField.options && searchField.options.optionList.map((option: any, index: number) =>
								<Option value={option.value} key={`option${searchField.name}${index}`}>
									{searchField.options?.type === 'codeBased' ?
										option.label :
										intl.formatMessage({ id: `${searchField.options?.alternateTranslationPrefix ? searchField.options.alternateTranslationPrefix : searchField.name}.${option.label}` })}
								</Option>
							)
						}
					</Select>
				);
			case "multiple":
				return (
					<Select
						{...field}
						mode="multiple"
						placeholder={searchField.placeholder || intl.formatMessage({ id: `formPlaceholders.${searchField.name}` })}
						style={{ width: "100%" }}
						filterOption={(input, option: any) =>
							option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
						}
						tagRender={({ label }) => (
							<Tag
								style={{
									border: "none",
									textTransform: "uppercase",
								}}
							>
								{label}
							</Tag>
						)}
					>
						{
							searchField.options && searchField.options.optionList.map((option: any, index: number) =>
								<Option value={option.value} key={`option${searchField.name}${index}`}>
									{searchField.options?.type === 'codeBased' ?
										option.label :
										intl.formatMessage({ id: `${searchField.options?.alternateTranslationPrefix ? searchField.options.alternateTranslationPrefix : searchField.name}.${option.label}` })}
								</Option>
							)
						}
					</Select>
				);
			case "checkbox":
				return <Checkbox {...field} checked={field.value} className={styles.checkbox}>{searchField.label || <FormattedMessage id={`formLabels.${searchField.name}`} />}</Checkbox>;
			case "date":
				return <DatePicker
					{...field}
					format={'DD MMM YYYY'}
					suffixIcon={false}
					style={{ padding: 0, width: '100%' }}
					picker='date'
				/>
			case "year":
				return <DatePicker
					{...field}
					format={'YYYY'}
					suffixIcon={false}
					style={{ padding: 0, width: '100%' }}
					picker='year'
				/>
			default:
				return <></>;
		}
	};

	return (
		<div className={styles.wrapper}>
			<div className={styles.filtersFormHeader}>
				<hr />
				<h3>{headerLabel || <FormattedMessage id="formHeaders.filterResults" />}</h3>
				<Button
					type="link"
					style={{ color: '#50BEE3' }}
					onClick={clickClearAll}
				>{clearAllLabel || <FormattedMessage id="buttons.clearAll" />}</Button>
			</div>

			<form onSubmit={handleSubmit(onSubmit)} className={styles.filtersForm}>
				<div className={styles.fields}>
					{fields.map((item: Field<T>, index) => (
						<div className={styles.fieldWrapper} key={`field${item.name}`}>
							{item.type !== 'checkbox' && <label className={styles.floatingLabel}>{item.label || <FormattedMessage id={`formLabels.${item.name}`} />}</label>}

							<Controller
								name={item.name}
								control={control}
								defaultValue={stickyValues ? stickyValues[item.name] : defaultValues[item.name]}
								rules={item.rules}
								render={({ field }) => {
									return renderFieldByType(item, field);
								}} />
						</div>
					))}
				</div>

				<Button
					htmlType="submit"
					type="primary"
					size="large"
					style={{ width: '100%' }}
				>
					{submitLabel || <FormattedMessage id="buttons.applyFilters" />}
				</Button>
			</form>
		</div>
	);
}

export default SearchForm;